/**
 * How a follow-up changes one detail of a conversation, declared once per kind
 * of detail (a date range, a Circle, a User, a Notice, who it's for, a day, a
 * Template, a Draft) rather than as hand-written steps in `replyTo.ts`. Most
 * slots only need `bare`, to recognise themselves in a message that asks for
 * nothing in particular ("same for Grade 3", "what about Friday?"); a few also
 * say how a direct answer to their own question is read (`direct`), reject one
 * that was never going to be a value at all (`rejects`), or offer themselves as
 * an answer's one named slot (`answerNameable`). `readFollowUp` is built from
 * these same rules, so a guided task (which calls it directly, never through
 * `replyTo.ts`) reads a bare follow-up exactly as an answer or a Handoff does.
 */
import { findDateRange, isDateFollowUp } from "./dateRange";
import { isPlausibleName, looksLikeQuestion } from "./sideQuestion";
import { DAY_PHRASE, findDay, tidy } from "./taskDetails";
import type { AnswerEntry, SlotName, SlotValues } from "./catalogue/types";

export type FollowUpRule = {
  /** Rejects a direct answer to this slot's own question, so it's asked again instead of taken as the value. */
  rejects?: (said: string) => boolean;
  /** This slot's new value in a bare follow-up that names no slot in particular; undefined when the message doesn't name this one. */
  bare?: (text: string) => string | undefined;
  /** How a direct answer to this slot's own question is read; the message itself, trimmed of end punctuation, by default. */
  direct?: (said: string) => string | undefined;
  /** Whether an answer can declare this as its one named slot, filled from a bare follow-up when nothing was specifically asked ("Which Circle?", "Which Notice?", "Which Draft?"). */
  answerNameable?: true;
};

/** What's said, trimmed of the spaces and end punctuation around it ("Grade 3?" is "Grade 3"). */
export const withoutEndPunctuation = (text: string): string => text.trim().replace(/[\s?.!]+$/, "");

/** Lead-in words a bare follow-up may start with: "same for Grade 3", "what about Friday?", "and tomorrow". The phrase after them, trimmed; null when the message isn't shaped like one at all. */
function bareFollowUpPhrase(text: string): string | null {
  const found = withoutEndPunctuation(text).match(
    /^(?:(?:and|now|ok|okay|also)\s+)?(?:(?:do\s+)?(?:the\s+)?same(?:\s+thing)?(?:\s+again)?\s+(?:for|to)|what\s+about|how\s+about|and(?:\s+(?:for|to))?|now(?:\s+(?:for|to))?|for|to)\s+(.+)$/i
  );
  return found ? tidy(found[1]) : null;
}

/** A day named in a bare follow-up phrase ("and Friday"), and nothing else in it. */
function bareDay(text: string): string | undefined {
  const phrase = bareFollowUpPhrase(text);
  if (!phrase) return undefined;
  const day = findDay(phrase);
  return day && !tidy(phrase.replace(DAY_PHRASE, "")) ? day : undefined;
}

/** A request to message someone, not the person's name ("dm one parent", "private message a user"). */
const NOT_A_PERSON =
  /\b(?:notice|messages?|send|something|dm|announcement)\b|\b(?:a|one|just one)\s+(?:mom|mum|parent|user)s?\b|\busers?\b/i;

/** Who or what's named in a bare follow-up phrase ("same for Grade 3", "what about Youth?"), when it isn't just a day. */
function bareNamed(text: string): string | undefined {
  const phrase = bareFollowUpPhrase(text);
  return phrase && !bareDay(text) ? phrase : undefined;
}

/** A date range named in a bare follow-up ("what about last month?"): the whole message has to be about the date for this to fire. */
function bareDateRange(text: string): string | undefined {
  return isDateFollowUp(text) ? findDateRange(text) ?? undefined : undefined;
}

export const FOLLOW_UP_RULES: Partial<Record<SlotName, FollowUpRule>> = {
  dateRange: { bare: bareDateRange, direct: (said) => findDateRange(said) ?? undefined },
  day: { bare: bareDay },
  audience: { bare: bareNamed },
  // A name step never takes a question or a long sentence as the name: it asks again instead
  member: { rejects: (said) => !isPlausibleName(said), bare: bareNamed, answerNameable: true },
  // An Award title never takes a question as the title: it asks again instead
  award: { rejects: (said) => looksLikeQuestion(said), bare: bareNamed, answerNameable: true },
  notice: { bare: bareNamed, answerNameable: true },
  circle: { bare: bareNamed, answerNameable: true },
  sender: { bare: bareNamed, answerNameable: true },
  child: { bare: bareNamed, answerNameable: true },
  // "dm one parent" and "send something to just one mom" are another way of asking, not a parent's name
  user: {
    rejects: (said) => looksLikeQuestion(said) || !isPlausibleName(said) || NOT_A_PERSON.test(said),
    bare: bareNamed,
    answerNameable: true,
  },
  role: { bare: bareNamed, answerNameable: true },
  worksheet: { bare: bareNamed, answerNameable: true },
  template: { bare: bareNamed },
  draft: { bare: bareNamed },
  // "put a file in drive" is how to upload, not the name of a file to find
  file: {
    rejects: (said) => /\b(?:upload|share)\b/i.test(said) || /\bput\b.+\b(?:file|folder|drive)\b/i.test(said),
    bare: bareNamed,
    answerNameable: true,
  },
};

/** Whether `slot`'s own rule rejects this direct answer, asking again rather than taking it as the value. */
export const followUpRejects = (slot: SlotName, said: string): boolean => FOLLOW_UP_RULES[slot]?.rejects?.(said) ?? false;

/** `slot`'s new value from a direct answer to its own question, read `slot`'s own way (undefined when it's unreadable, e.g. no date in it at all). */
export const readDirectFollowUp = (slot: SlotName, text: string): string | undefined =>
  (FOLLOW_UP_RULES[slot]?.direct ?? withoutEndPunctuation)(text) || undefined;

/** `slot`'s new value from a bare follow-up that names no slot in particular, `slot`'s own way; undefined when the message doesn't name it. */
export const readBareFollowUp = (slot: SlotName, text: string): string | undefined => FOLLOW_UP_RULES[slot]?.bare?.(text);

/** `{ [slot]: value }`, typed as `SlotValues`. Every slot `readDirectFollowUp`/`readBareFollowUp` can fill holds free text (a date range or day key is itself a validated string), so this is the one place that trusts the shape. */
export const slotValue = (slot: SlotName, value: string): SlotValues => ({ [slot]: value } as SlotValues);

/**
 * The detail a bare follow-up with nothing specifically asked changes by
 * default, for a guided task or a Handoff that declares no slot of its own: a
 * day first ("and Friday"), then who it's for ("same for Grade 3").
 */
const DEFAULT_BARE_ORDER: SlotName[] = ["day", "audience"];

/**
 * The one detail a follow-up changes: "Same for Grade 3", "what about Grade 3?",
 * "and tomorrow". Null when the message isn't shaped like a follow-up. Used by
 * a guided task directly, and by `replyTo.ts` for a Handoff with no slot of its
 * own declared (`followUpSlot`) to change.
 */
export function readFollowUp(text: string): SlotValues | null {
  for (const slot of DEFAULT_BARE_ORDER) {
    const value = readBareFollowUp(slot, text);
    if (value !== undefined) return slotValue(slot, value);
  }
  return null;
}

/** Every slot an answer can declare as its one named slot, in the order checked were it ever to declare more than one. */
const ANSWER_NAMEABLE_SLOTS = (Object.keys(FOLLOW_UP_RULES) as SlotName[]).filter(
  (slot) => FOLLOW_UP_RULES[slot]?.answerNameable
);

/** The one named slot this answer declares ("Which Circle?", "Which Notice?", "Which Draft?"), if it has one. */
export const namedSlotOf = (entry: AnswerEntry): SlotName | undefined =>
  ANSWER_NAMEABLE_SLOTS.find((slot) => entry.slots?.[slot] !== undefined);
